import { App } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { UnresolvedVendorErrorCodeResponseDto } from '@evimo/client/models';
import type { UnresolvedStatusFilter } from '~/app/components/errorManagement/UnresolvedVendorErrorCodeTab/types';
import { useUnresolvedCatalogOptions } from '~/app/components/errorManagement/UnresolvedVendorErrorCodeTab/hooks/useUnresolvedCatalogOptions';
import { useUnresolvedEditDrawer } from '~/app/components/errorManagement/UnresolvedVendorErrorCodeTab/hooks/useUnresolvedEditDrawer';
import { useUnresolvedRemarkActions } from '~/app/components/errorManagement/UnresolvedVendorErrorCodeTab/hooks/useUnresolvedRemarkActions';
import { useDebouncedValue } from '~/app/hooks/useDebouncedValue';
import { UnresolvedVendorErrorCodeService } from '~/app/services/errorManagement/unresolved-vendor-error-code.service';

export function useUnresolvedVendorErrorCodeTab() {
  const { t } = useTranslation('errorManagement');
  const { message } = App.useApp();
  const { manufacturerOptions, assetTypeOptions } =
    useUnresolvedCatalogOptions();
  const { isRemarkActionPending, openRemarkActionModal } =
    useUnresolvedRemarkActions();
  const editDrawer = useUnresolvedEditDrawer();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearch = useDebouncedValue(searchValue, 300);
  const [manufacturerFilter, setManufacturerFilter] = useState<
    string | undefined
  >();
  const [statusFilter, setStatusFilter] =
    useState<UnresolvedStatusFilter>(undefined);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [isViewDrawerOpen, setIsViewDrawerOpen] = useState(false);
  const [viewingRecord, setViewingRecord] =
    useState<UnresolvedVendorErrorCodeResponseDto | null>(null);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, manufacturerFilter, statusFilter, sortOrder]);

  const listQuery = useQuery({
    queryKey: [
      'unresolved-vendor-error-codes',
      page,
      pageSize,
      debouncedSearch,
      manufacturerFilter,
      statusFilter,
      sortOrder,
    ],
    queryFn: () =>
      UnresolvedVendorErrorCodeService.list({
        page,
        limit: pageSize,
        ...(debouncedSearch.trim() ? { q: debouncedSearch.trim() } : {}),
        ...(manufacturerFilter ? { manufacturerId: manufacturerFilter } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
        sortOrder,
      }),
    staleTime: 0,
    refetchOnMount: 'always',
  });

  useEffect(() => {
    if (listQuery.isError) {
      message.error(t('unresolved.messages.loadFailed'));
    }
  }, [listQuery.isError, message, t]);

  return {
    rows: listQuery.data?.data ?? [],
    total: listQuery.data?.meta.total ?? 0,
    page,
    pageSize,
    searchValue,
    manufacturerFilter,
    statusFilter,
    manufacturerOptions,
    assetTypeOptions,
    sortOrder,
    isLoading: listQuery.isLoading,
    isDrawerOpen: editDrawer.isDrawerOpen,
    drawerRecord: editDrawer.drawerRecord,
    isViewDrawerOpen,
    viewingRecord,
    hasConflict: editDrawer.hasConflict,
    isSubmitting: editDrawer.isSubmitting || isRemarkActionPending,
    onSearchChange: (value: string) => {
      setSearchValue(value);
    },
    onManufacturerFilterChange: (value: string | undefined) => {
      setManufacturerFilter(value || undefined);
    },
    onStatusFilterChange: (value: UnresolvedStatusFilter) => {
      setStatusFilter(value);
    },
    onPageChange: (nextPage: number, nextPageSize: number) => {
      setPage(nextPage);
      setPageSize(nextPageSize);
    },
    onToggleSortOrder: () => {
      setSortOrder((current) => (current === 'desc' ? 'asc' : 'desc'));
    },
    onView: (record: UnresolvedVendorErrorCodeResponseDto) => {
      editDrawer.closeEditDrawer();
      setViewingRecord(record);
      setIsViewDrawerOpen(true);
    },
    onEdit: (record: UnresolvedVendorErrorCodeResponseDto) => {
      setIsViewDrawerOpen(false);
      setViewingRecord(null);
      editDrawer.openEdit(record);
    },
    onResolve: (record: UnresolvedVendorErrorCodeResponseDto) => {
      openRemarkActionModal('resolve', record);
    },
    onIgnore: (record: UnresolvedVendorErrorCodeResponseDto) => {
      openRemarkActionModal('ignore', record);
    },
    onUnignore: (record: UnresolvedVendorErrorCodeResponseDto) => {
      openRemarkActionModal('unignore', record);
    },
    onDrawerClose: editDrawer.closeEditDrawer,
    onViewDrawerClose: () => {
      setIsViewDrawerOpen(false);
      setViewingRecord(null);
    },
    onDrawerSave: editDrawer.handleSave,
    onReloadServerCopy: editDrawer.reloadServerCopy,
  };
}
